import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';

interface AutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: (e?: React.FormEvent) => void;
  suggestions: string[];
  placeholder?: string;
  className?: string;
  removeFromHistory?: (url: string) => void; // Changed from clearHistory to removeFromHistory
}

/**
 * Autocomplete Component
 * 
 * A reusable component that provides URL input with autocomplete functionality.
 * Features:
 * - Shows recent URLs as suggestions
 * - Allows keyboard navigation (arrow keys, Enter, Escape)
 * - Allows removing individual URLs from history
 * - Filters suggestions based on current input
 * - Handles click outside to close suggestions
 */
const Autocomplete: React.FC<AutocompleteProps> = ({
  value,
  onChange,
  onSubmit,
  suggestions,
  placeholder = '',
  className = '',
  removeFromHistory
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Filter suggestions based on input value
  // This filters the suggestions array to only include items that contain the current input value
  // Case-insensitive matching is used for better user experience
  // Exclude exact matches to avoid showing redundant suggestions
  const filteredSuggestions = suggestions.filter(
    suggestion => {
      // Don't show suggestions that exactly match the current input
      if (suggestion === value) return false;
      // Show suggestions that contain the current input as a substring
      return suggestion.toLowerCase().includes(value.toLowerCase());
    }
  );

  // Handle clicks outside the component to close the suggestions dropdown
  // This improves UX by hiding suggestions when user clicks elsewhere on the page
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle keyboard navigation for accessibility and improved UX
  // Supports: Arrow Up/Down (navigate suggestions), Enter (select), Escape (close)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Down arrow - Move to next suggestion
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestion(prev => 
        prev < filteredSuggestions.length - 1 ? prev + 1 : prev
      );
    }
    // Up arrow - Move to previous suggestion
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestion(prev => (prev > 0 ? prev - 1 : 0));
    }
    // Enter - Select current suggestion
    else if (e.key === 'Enter') {
      if (activeSuggestion >= 0) {
        e.preventDefault();
        onChange(filteredSuggestions[activeSuggestion]);
        setShowSuggestions(false);
        if (onSubmit) onSubmit(e);
      }
    }
    // Escape - Close suggestions dropdown
    else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setShowSuggestions(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white ${className}`}
        autoComplete="off"
      />
      
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          <ul className="py-1">
            {filteredSuggestions.map((suggestion, index) => (
              <li
                key={index}
                className={`px-4 py-2 text-sm cursor-pointer flex justify-between items-center ${index === activeSuggestion ? 'bg-blue-100 dark:bg-blue-900/30' : 'hover:bg-gray-100 dark:hover:bg-gray-700/30'}`}
              >
                {/* URL suggestion text - clickable to select */}
                <span 
                  className="flex-1 truncate mr-2"
                  onClick={() => {
                    onChange(suggestion);
                    setShowSuggestions(false);
                  }}
                >
                  {suggestion}
                </span>
                
                {/* Delete button (X) for each URL */}
                {removeFromHistory && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromHistory(suggestion);
                    }}
                    className="text-gray-400 hover:text-red-500 focus:outline-none"
                    type="button"
                    aria-label="Remove URL"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Autocomplete;
